// Deep-link-aware login redirect helpers (UX review finding #6).
//
// Routes that require an authenticated user (`/billing`, `/progress`,
// `/library`, `/signup`, `/review/[id]`,
// `/sessions/[id]/review`, the AI-Review gate on `/record-review`, etc.)
// were silently bouncing logged-out visitors to a context-free
// `/login`. After this module:
//
//   1. Guards build the redirect via `buildLoginRedirect(path, search)` so
//      every caller emits the same `?return=<safe-pathname>` shape.
//   2. `/login` reads the param, shows a contextual note above the email
//      field ("Sign in to continue to <human-readable>"), and navigates the
//      user back to the requested route after a successful sign-in.
//   3. The return URL is validated as a same-origin pathname — any value
//      that could re-target the browser to an external site is rejected
//      and falls back to `/`.
//
// The contextual labels live here, alongside the validator, so the login
// page does not have to import a route map separately.

import type { PathnameWithSearchOrHash } from '$app/types';

/**
 * Human-readable labels for the small set of well-known auth-gated routes.
 * The login page renders "Sign in to continue to <label>" — the label must
 * read naturally after that fragment, lowercase, no terminal period.
 *
 * Keep this list tight: matching is by exact pathname, so a new gated route
 * only needs an entry when its destination should be named on the login
 * page. Unknown paths fall through to `DEFAULT_RETURN_LABEL`.
 */
export const RETURN_URL_CONTEXTS: Readonly<Record<string, string>> = Object.freeze({
  '/billing': 'your billing settings',
  '/progress': 'your progress',
  '/library': 'your library',
  '/signup': 'creating your account',
  '/record-review': 'Record & Review',
});

/** Fallback label when the return path is not in `RETURN_URL_CONTEXTS`. */
export const DEFAULT_RETURN_LABEL = 'your account';

// Matches control characters (U+0000 – U+001F) and DEL (U+007F). Pulled out
// as a literal so the regex source itself documents the byte range; using a
// character class with literal control bytes would parse correctly but read
// as garbage in the source listing.
// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_PATTERN = /[ -]/;

/**
 * Validates that a candidate return URL is a same-origin relative path.
 * Anything that could let an attacker bounce the freshly-signed-in user
 * off-site (protocol-relative `//evil.com`, absolute `https://evil.com`,
 * windows-style backslash bypass `\\evil.com`, or a missing leading `/`)
 * is rejected. Returns `'/'` for invalid input — the safe default that
 * also matches the historical post-login destination.
 *
 * The check is intentionally strict — we do not parse via `new URL()` and
 * compare origins because the SPA runs on Cloudflare Pages where any
 * non-local origin would already mean trouble.
 */
export function validateReturnUrl(
  candidate: string | null | undefined,
): PathnameWithSearchOrHash {
  if (!candidate) return '/';
  // Reject anything containing a backslash — Windows-style separators have
  // bypassed naive URL parsers in the past (e.g. `/\evil.com`).
  if (candidate.includes('\\')) return '/';
  // Reject protocol-relative (`//host/...`) and any explicit scheme.
  if (candidate.startsWith('//')) return '/';
  if (/^[a-z][a-z0-9+.-]*:/i.test(candidate)) return '/';
  // Must be an absolute path on this origin.
  if (!candidate.startsWith('/')) return '/';
  // Defence in depth: a path with control characters is suspicious.
  if (CONTROL_CHAR_PATTERN.test(candidate)) return '/';
  // The accepted value is handed to SvelteKit's `resolve()`, which reads a
  // `[name]` segment as a route parameter and throws when no value is supplied.
  if (candidate.includes('[')) return '/';
  // This is the trust boundary: everything above rejects anything that is not a
  // same-origin absolute path, which is exactly what the route type asserts.
  return candidate as PathnameWithSearchOrHash;
}

/**
 * Returns the contextual label for a return URL, or the default fallback.
 * Uses only the pathname portion (a query string on the return URL does
 * not change the destination's human name).
 */
export function returnUrlLabel(returnUrl: string | null | undefined): string {
  const safe = validateReturnUrl(returnUrl);
  if (safe === '/') return DEFAULT_RETURN_LABEL;
  const pathOnly = safe.split('?')[0];
  return RETURN_URL_CONTEXTS[pathOnly] ?? DEFAULT_RETURN_LABEL;
}

/**
 * Builds a `/login?return=<encoded>` URL. Pass the current pathname (and
 * optional search string) — typically `$page.url.pathname + $page.url.search`.
 * The path itself is validated so a caller passing a broken candidate
 * cannot accidentally emit a URL that the login page will then reject.
 */
export function buildLoginRedirect(
  pathname: string,
  search: string = '',
): '/login' | `/login?return=${string}` {
  const safe = validateReturnUrl(pathname + search);
  if (safe === '/') return '/login';
  return `/login?return=${encodeURIComponent(safe)}`;
}

/**
 * Build the /login href that returns the user to `currentPath` after login.
 * Pass `url.pathname + url.search`. Returns a bare `/login` (post-login →
 * home) when there is nothing meaningful to return to — i.e. when the
 * current page is itself an auth route (`/login`, `/signup`) or the home
 * page `/` — to avoid redirect loops and a pointless `?return=/`.
 */
export function loginHref(
  currentPath: string | null | undefined,
): '/login' | `/login?return=${string}` {
  const p = (currentPath ?? '').trim();
  if (!p || p === '/' || p.startsWith('/login') || p.startsWith('/signup')) {
    return '/login';
  }
  return `/login?return=${encodeURIComponent(p)}`;
}
